import { CogNeedTextField } from '@cogneed/react-utils';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMoreRounded';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Fade,
  FormControl,
  MenuItem,
  Typography,
} from '@mui/material';
import { isNaN } from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import useFreeResiliationFormulaContext from '../../Contexts/FreeResiliationFormulaContext/useFreeResiliationFormulaContext';
import CogNeedKeyboardDatePicker from '../../../../../../CogNeedKeyboardDatePicker';

const SECTION_KEY = 'price';

export default function PriceSection() {
  const { t, i18n } = useTranslation('wysiwyg');
  const monthFormat = new Intl.NumberFormat(i18n.language, { style: 'unit', unit: 'month', unitDisplay: 'long' });

  const {
    state, updateState, setExpanded, expanded, completedSteps: { price: isCompleted },
  } = useFreeResiliationFormulaContext();

  function setPrice(value: number) { updateState((prev) => ({ ...prev, normalPrice: value })); }
  function setPromotionDuration(value: 6 | 12 | 0) {
    updateState((prev) => ({ ...prev, promotionDuration: value, ...value === 0 && { promotionPrice: undefined } }));
  }
  function setPromoPrice(value: number) {
    updateState((prev) => ({ ...prev, promotionPrice: value }));
  }
  function setStartDate(value: Date) { updateState((prev) => ({ ...prev, startDate: value })); }

  return (
    <Accordion
      expanded={expanded === SECTION_KEY}
      onChange={() => { setExpanded(expanded === SECTION_KEY ? null : SECTION_KEY); }}
      className="section"
      elevation={0}
      TransitionProps={{ unmountOnExit: true }}
    >
      <AccordionSummary expandIcon={(!isCompleted || expanded === SECTION_KEY) ? <ExpandMoreIcon /> : <CheckCircleOutlineIcon className="checked" />}>
        {t('resiliation-fee.price-section.title')}
      </AccordionSummary>
      <AccordionDetails>
        <FormControl>
          <Typography variant="h4">
            {t('resiliation-fee.price-section.promotion.duration')}
          </Typography>
          <CogNeedTextField
            select
            onChange={(e) => {
              const value = Number(e.target.value).valueOf() as 6 | 12 | 0;
              setPromotionDuration(value);
            }}
            defaultValue={state.promotionDuration ?? ''}
            SelectProps={{
              MenuProps: {
                anchorOrigin: { vertical: 'bottom', horizontal: 2 },
                transformOrigin: { vertical: -6, horizontal: 2 },
              },
              displayEmpty: true,
              renderValue(value): React.ReactNode {
                if (value === '') { return t('resiliation-fee.price-section.promotion.placeholder'); }
                if (value === 0) { return <>Pas de promotion</>; }

                // eslint-disable-next-line react/jsx-no-useless-fragment
                return <>{monthFormat.format(parseInt(value as string, 10))}</>;
              },
            }}
          >
            {[0, 6, 12].map((value) => (
              <MenuItem key={value} value={value}>
                {value ? monthFormat.format(value) : 'Pas de promotion'}
              </MenuItem>
            ))}
          </CogNeedTextField>
        </FormControl>
        <Fade in={!!state.promotionDuration} mountOnEnter unmountOnExit>
          <FormControl>
            <Typography variant="h4">
              {t('resiliation-fee.price-section.promotion.price')}
            </Typography>
            {state.promotionDuration && (
              <CogNeedTextField
                sx={{ width: 100 }}
                type="number"
                defaultValue={state.promotionPrice ?? ''}
                onChange={(e) => {
                  const value = Number(e.target.value).valueOf();
                  if (!isNaN(value)) {
                    setPromoPrice(value);
                  }
                }}
                InputProps={{
                  inputProps: { min: 0 },
                  endAdornment: <Typography variant="body1">€</Typography>,
                }}
              />
            )}
          </FormControl>
        </Fade>
        <Fade in={state.promotionDuration !== undefined} mountOnEnter unmountOnExit>
          <FormControl>
            <Typography variant="h4">
              {t('resiliation-fee.price-section.price.title')}
            </Typography>
            <CogNeedTextField
              sx={{ width: 100 }}
              type="number"
              defaultValue={state.normalPrice ?? ''}
              onChange={(e) => {
                const value = Number(e.target.value).valueOf();
                if (!isNaN(value)) {
                  setPrice(value);
                }
              }}
              InputProps={{
                inputProps: { min: 0 },
                endAdornment: <Typography variant="body1">€</Typography>,
              }}
            />
          </FormControl>
        </Fade>
        <Fade in={!!state.promotionDuration && !state.commitmentDuration} mountOnEnter unmountOnExit>
          <FormControl>
            <Typography variant="h4">
              {t('resiliation-fee.price-section.promotion.start')}
            </Typography>
            {!!state.promotionDuration && (
              <CogNeedKeyboardDatePicker
                disableFuture
                inputFormat="dd/MM/yyyy"
                value={state.startDate ?? null}
                InputProps={{
                  sx: { width: 175 },
                }}
                onChange={(date: Date | null) => {
                  if (date && !isNaN(date.getTime())) {
                    setStartDate(date);
                  }
                }}
              />
            )}
          </FormControl>
        </Fade>
        <Box className="actions">
          {isCompleted && (
            <Button onClick={() => { setExpanded('new-offer'); }} color="info" variant="outlined">
              {t('resiliation-fee.next')}
            </Button>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
